import { useEffect, useRef, useState } from 'react';
import { formatUnits, parseUnits, type Hex } from 'viem';
import { ChevronDown, X, Loader2, ArrowUpDown } from 'lucide-react';
import {
  createSwapClient,
  executeSwap,
  fmt,
  fmtUsd,
  SAN_ADDRESS,
  useQuote,
  useTokens,
  type Token,
  type TokenWithBalance,
} from '@/modules/swap';
import { isSameAddress } from '@/modules/is-same-address';
import { useDebouncedValue } from '@/modules/debounce';
import { wagmiConfig } from '@/shared/utils/wagmiConfig';
import { track, type Events } from '@/shared/utils/track';
import { sanitizeDecimalInput } from '@/modules/sanitize-decimal-input';

const swapClient = createSwapClient(wagmiConfig);

type Props = {
  address: Hex;
  lockToSan?: boolean;
  context: Events['swap_attempted']['context'];
  /** `receivedAmount` is human-decimal, not wei. */
  onSuccess: (receivedAmount: string, toToken: TokenWithBalance) => void;
};

type Direction = 'buy' | 'sell';

export default function SwapForm({
  address,
  lockToSan,
  context,
  onSuccess,
}: Props) {
  const {
    tokens,
    sanToken,
    loading: tokensLoading,
    refetch,
  } = useTokens(swapClient, address);

  const [direction, setDirection] = useState<Direction>('buy');
  const [selected, setSelected] = useState<TokenWithBalance | null>(null);
  const [amount, setAmount] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [status, setStatus] = useState('');
  const [execError, setExecError] = useState('');

  // A guarded render-time state adjustment (React's "adjusting state when
  // props change" pattern): the non-null `selected` blocks any further writes.
  if (tokens.length > 0 && !selected) {
    const defaultToken =
      tokens.find((t) => !isSameAddress(t.address, SAN_ADDRESS)) ?? tokens[0];
    if (defaultToken) setSelected(defaultToken);
  }

  const fromToken = direction === 'buy' ? selected : sanToken;
  const toToken = direction === 'buy' ? sanToken : selected;

  const {
    route,
    loading: quoteLoading,
    error: quoteError,
  } = useQuote(swapClient, fromToken, toToken, amount, address);

  const receiveAmount =
    route ? formatUnits(BigInt(route.toAmount), toToken?.decimals ?? 18) : '';

  const insufficientBalance = (() => {
    if (!fromToken || amount === '') return false;
    try {
      return parseUnits(amount, fromToken.decimals) > fromToken.bal;
    } catch {
      return false;
    }
  })();

  const handleAmountChange = (val: string) => {
    setAmount(sanitizeDecimalInput(val));
  };

  const handleFlip = () => {
    if (lockToSan === true) return;
    const next: Direction = direction === 'buy' ? 'sell' : 'buy';
    track('swap_direction_flipped', { direction: next });
    setDirection(next);
    setAmount('');
  };

  // The invisible middle of the swap funnel: modal opened but no viable
  // quote (no route / quote error) — attributes modal→attempt drop-off.
  useEffect(() => {
    if (quoteError !== '') track('swap_quote_failed', { reason: quoteError });
  }, [quoteError]);

  const handleConfirm = async () => {
    if (!route || !fromToken || !toToken) return;
    setExecuting(true);
    setExecError('');
    setStatus('Preparing...');
    const fromSymbol = fromToken.symbol;
    const toSymbol = toToken.symbol;
    const fromAmount = amount;
    let signedReported = false;
    const result = await executeSwap(swapClient, route, (msg) => {
      if (msg === '__signed__') {
        if (!signedReported) {
          signedReported = true;
          track('swap_attempted', {
            status: 'signed',
            from_symbol: fromSymbol,
            to_symbol: toSymbol,
            from_amount: fromAmount,
            context,
          });
        }
        return;
      }
      setStatus(msg);
    });
    setExecuting(false);
    setStatus('');
    if (result.ok) {
      track('swap_attempted', {
        status: 'mined',
        from_symbol: fromSymbol,
        to_symbol: toSymbol,
        from_amount: fromAmount,
        context,
      });
      // The EXECUTED amount, not the quote-time route.toAmount — with rate
      // updates the two differ, and the success message must report what
      // actually arrived.
      const received = formatUnits(
        BigInt(result.receivedAmount),
        toToken.decimals,
      );
      setAmount('');
      void refetch();
      onSuccess(received, toToken);
    } else {
      track('swap_attempted', {
        status: result.rejected ? 'rejected' : 'error',
        from_symbol: fromSymbol,
        to_symbol: toSymbol,
        from_amount: fromAmount,
        context,
      });
      setExecError(result.error);
    }
  };

  const canConfirm =
    !!route && !executing && !insufficientBalance && !quoteLoading;

  const buttonLabel =
    executing ?
      status !== '' ?
        status
      : 'Confirming...'
    : insufficientBalance ? 'Insufficient balance'
    : quoteLoading ? 'Getting quote...'
    : amount === '' || Number.parseFloat(amount) === 0 ? 'Enter amount'
    : 'Confirm swap';

  return (
    // `relative` contains the tokensLoading overlay to the form: without it
    // the overlay anchors to the Modal card and covers the header + close
    // cross, locking the modal shut while tokens load.
    <div className="relative flex flex-col gap-s">
      <Panel
        label="Swap from"
        amountValue={amount}
        onAmountChange={handleAmountChange}
        token={fromToken}
        canPickToken={direction === 'buy'}
        onPickToken={() => setSelectorOpen(true)}
        balanceText={
          fromToken ?
            `${fmt(fromToken.formatted)} ${fromToken.symbol} available`
          : ''
        }
        onMax={
          fromToken && fromToken.symbol !== 'ETH' && fromToken.bal > BigInt(0) ?
            () => setAmount(fromToken.formatted)
          : undefined
        }
      />

      <div className="flex justify-center">
        {lockToSan === true ?
          <div className="flex h-8 w-8 items-center justify-center rounded-full text-gray">
            <ArrowUpDown size={16} />
          </div>
        : <button
            type="button"
            onClick={handleFlip}
            aria-label="Flip swap direction"
            className="btn-reset flex h-8 w-8 items-center justify-center rounded-full text-gray hover:text-purple-default"
          >
            <ArrowUpDown size={16} />
          </button>
        }
      </div>

      <Panel
        label="To"
        amountValue={
          quoteLoading ? ''
          : receiveAmount !== '' ?
            fmt(receiveAmount)
          : ''
        }
        readOnly
        token={toToken}
        canPickToken={lockToSan !== true && direction === 'sell'}
        onPickToken={() => setSelectorOpen(true)}
        balanceText={
          amount !== '' && fromToken ? fmtUsd(amount, fromToken.priceUSD) : ''
        }
        loading={quoteLoading}
      />

      {quoteError !== '' && (
        <div className="text-body-sm text-red-default">{quoteError}</div>
      )}
      {execError !== '' && (
        <div className="text-body-sm text-red-default">{execError}</div>
      )}

      <div className="mt-xs text-center text-body-sm text-gray">
        Powered by LiFi · best route across DEX aggregators
      </div>

      <button
        type="button"
        onClick={() => {
          void handleConfirm();
        }}
        disabled={!canConfirm}
        className="mt-s btn-reset flex w-full items-center justify-center gap-s rounded-full bg-purple-default py-m text-body-lg font-bold text-white hover:bg-purple-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {executing && <Loader2 size={18} className="animate-spin" />}
        {buttonLabel}
      </button>

      {tokensLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background-secondary/80 text-body-md text-gray">
          <Loader2 size={20} className="mr-s animate-spin" />
          Loading tokens...
        </div>
      )}

      {selectorOpen && (
        <TokenSelector
          tokens={tokens.filter((t) => !isSameAddress(t.address, SAN_ADDRESS))}
          onSelect={(t) => {
            setSelected(t);
            setSelectorOpen(false);
            setAmount('');
          }}
          onClose={() => setSelectorOpen(false)}
        />
      )}
    </div>
  );
}

function Panel({
  label,
  amountValue,
  onAmountChange,
  token,
  canPickToken,
  onPickToken,
  balanceText,
  onMax,
  readOnly,
  loading,
}: {
  label: string;
  amountValue: string;
  onAmountChange?: ((v: string) => void) | undefined;
  token: TokenWithBalance | null;
  canPickToken: boolean;
  onPickToken: () => void;
  balanceText: string;
  onMax?: (() => void) | undefined;
  readOnly?: boolean;
  loading?: boolean;
}) {
  return (
    <div className="rounded-lg bg-background-main p-l">
      <div className="flex items-start justify-between">
        <span className="text-body-md text-gray">{label}</span>
        {token && (
          <button
            type="button"
            onClick={canPickToken ? onPickToken : undefined}
            disabled={!canPickToken}
            className="btn-reset flex items-center gap-s rounded-full bg-background-secondary px-s py-1 text-body-md font-medium text-gray-text disabled:cursor-default"
          >
            <TokenIcon key={token.address} token={token} size={20} />
            <span>{token.symbol}</span>
            {canPickToken && <ChevronDown size={14} />}
          </button>
        )}
      </div>
      <div className="relative mt-s">
        {readOnly === true ?
          <div className="text-display font-medium text-gray-text">
            {loading === true ?
              <Loader2 size={24} className="animate-spin text-gray" />
            : amountValue !== '' ?
              amountValue
            : <span className="text-gray-3">0</span>}
          </div>
        : <input
            type="text"
            inputMode="decimal"
            placeholder="0"
            value={amountValue}
            onChange={(e) => onAmountChange?.(e.target.value)}
            className="w-full bg-transparent text-display font-medium text-gray-text outline-none placeholder:text-gray-3"
          />
        }
      </div>
      {(balanceText !== '' || onMax) && (
        <div className="mt-s flex items-center justify-between text-body-md text-gray">
          <span>{balanceText}</span>
          {onMax && (
            <button
              type="button"
              onClick={onMax}
              className="btn-reset font-medium text-gray hover:text-purple-default"
            >
              Swap MAX
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// LiFi hosts the major assets' logos (ETH, USDT, USDC, WBTC, …) on
// raw.githubusercontent.com, which rate-limits and is flaky on some networks —
// serve those through jsdelivr's GitHub mirror, falling back to the spothq
// icon set by symbol, then the untouched original URL (the first two are
// both on cdn.jsdelivr.net; the original guards against a jsdelivr outage).
// Tokens without a logoURI render the letters badge directly — keying an
// icon on symbol alone would put the canonical asset's icon on unrelated
// same-symbol tokens (e.g. "OpenEden USDC Vault", symbol USDC, no logo).
const GITHUB_RAW_RE =
  /^https:\/\/raw\.githubusercontent\.com\/([^/]+)\/([^/]+)\/([^/]+)\//;

export function iconSources(token: Token): string[] {
  if (token.logoURI == null || token.logoURI === '') return [];
  const mirrored = token.logoURI.replace(
    GITHUB_RAW_RE,
    'https://cdn.jsdelivr.net/gh/$1/$2@$3/',
  );
  const sources = [
    mirrored,
    `https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons/32/color/${token.symbol.toLowerCase()}.png`,
  ];
  if (mirrored !== token.logoURI) sources.push(token.logoURI);
  return sources;
}

export function TokenIcon({
  token,
  size = 24,
}: {
  token: Token;
  size?: number;
}) {
  const [srcIdx, setSrcIdx] = useState(0);
  const sources = iconSources(token);
  if (srcIdx < sources.length) {
    return (
      <img
        src={sources[srcIdx]}
        alt={token.symbol}
        width={size}
        height={size}
        loading="lazy"
        className="rounded-full"
        onError={() => setSrcIdx(srcIdx + 1)}
      />
    );
  }
  return (
    <div
      className="flex items-center justify-center rounded-full bg-purple-01 text-body-sm font-bold text-purple-default"
      style={{ width: size, height: size }}
    >
      {token.symbol.slice(0, 2)}
    </div>
  );
}

export function TokenSelector({
  tokens,
  onSelect,
  onClose,
}: {
  tokens: TokenWithBalance[];
  onSelect: (t: TokenWithBalance) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<globalThis.HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const debouncedQuery = useDebouncedValue(query, 200);

  const filtered = (() => {
    if (debouncedQuery === '') return tokens.filter((t) => t.bal > BigInt(0));
    const q = debouncedQuery.toLowerCase();
    const matches: TokenWithBalance[] = [];
    for (const t of tokens) {
      if (
        t.symbol.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        isSameAddress(t.address, q)
      ) {
        matches.push(t);
        if (matches.length >= 50) break;
      }
    }
    return matches;
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-100 rounded-md bg-background-secondary p-l"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-m flex items-center justify-between">
          <span className="text-body-lg font-bold text-gray-text">
            Select token
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="btn-reset text-gray hover:text-gray-text"
          >
            <X size={18} />
          </button>
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search by name or address"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mb-m w-full rounded-sm border border-gray-5 bg-background-main px-m py-s text-body-md text-gray-text outline-none placeholder:text-gray-3 focus:border-purple-default"
        />
        <div className="max-h-80 overflow-y-auto">
          {filtered.map((t) => {
            const bal = Number.parseFloat(t.formatted);
            return (
              <button
                key={t.address}
                type="button"
                onClick={() => onSelect(t)}
                className="btn-reset flex w-full items-center gap-m rounded-sm px-m py-s hover:bg-background-main"
              >
                <TokenIcon token={t} size={32} />
                <div className="flex-1 text-left">
                  <div className="text-body-md font-medium text-gray-text">
                    {t.symbol}
                  </div>
                  <div className="text-body-sm text-gray">{t.name}</div>
                </div>
                {bal > 0 && (
                  <div className="text-right">
                    <div className="text-body-sm text-gray-text">
                      {fmt(t.formatted)}
                    </div>
                    {fmtUsd(t.formatted, t.priceUSD) !== '' && (
                      <div className="text-body-micro text-gray">
                        {fmtUsd(t.formatted, t.priceUSD)}
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-xl text-center text-body-md text-gray">
              {query !== '' ? 'No tokens found' : 'No tokens with balance'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
